import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Circle, Copy, Loader2, Lock, LogOut, PhoneOff, RefreshCw, ShieldAlert, Square, X } from "lucide-react";
import { toast } from "sonner";
import { JitsiFrame } from "@/components/JitsiFrame";
import { Wordmark } from "@/components/Nav";
import { useAuth } from "@/context/AuthContext";
import { fmtClock, useRecorder } from "@/hooks/useRecorder";
import { useLang } from "@/i18n";
import { api, errCode } from "@/lib/api";

export default function Room() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const callId = params.get("call");
  const navigate = useNavigate();
  const { config } = useAuth();
  const { t } = useLang();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [call, setCall] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const jitsiRef = useRef(null);

  useEffect(() => {
    if (!info) return undefined;
    const started = Date.now();
    const timer = setInterval(() => setElapsed(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => clearInterval(timer);
  }, [info]);

  useEffect(() => {
    if (!callId) return undefined;
    let alive = true;
    const tick = () => api.get(`/calls/${callId}`).then((r) => {
      if (!alive) return;
      setCall(r.data);
      if (r.data.status !== "ringing") clearInterval(timer);
      if (["declined", "missed", "cancelled"].includes(r.data.status)) toast(t(`contacts.${r.data.status}`));
    }).catch(() => clearInterval(timer));
    const timer = setInterval(tick, 3000);
    tick();
    return () => { alive = false; clearInterval(timer); };
  }, [callId, t]);
  const cancelCall = async () => { try { await api.post(`/calls/${callId}/cancel`); } catch { /* noop */ } };

  useEffect(() => {
    api.post(`/meetings/${id}/join`).then((r) => setInfo(r.data)).catch((e) => setError(t(`errors.${errCode(e)}`, errCode(e))));
  }, [id, t]);

  const onFinished = useCallback((data) => navigate(`/meetings/${id}?job=${data.job_id}`), [navigate, id]);
  const rec = useRecorder({ meetingId: id, onFinished });

  const announce = (on) => {
    const j = jitsiRef.current;
    if (!j || !info) return;
    try {
      j.executeCommand("subject", on ? `${t("room.recSubject")} · ${info.title}` : info.title);
      if (on) j.executeCommand("sendChatMessage", t("room.recChatMsg"));
    } catch { /* noop */ }
  };
  const startRec = async () => {
    if (!window.confirm(t("room.recConfirm"))) return;
    await rec.start();
  };
  useEffect(() => {
    if (rec.state === "recording") announce(true);
    if (rec.state === "finishing") announce(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rec.state]);

  const leave = () => navigate(`/meetings/${id}`);
  const endMeeting = async () => {
    if (rec.state === "recording") await rec.stop();
    await api.post(`/meetings/${id}/end`).catch(() => {});
    navigate(`/meetings/${id}`);
  };
  const copyGuest = () => {
    navigator.clipboard.writeText(`${window.location.origin}/join/${id}?code=${info.join_code}`);
    toast.success(t("common.copied"));
  };
  const rotateGuest = async () => {
    if (!window.confirm(t("room.rotateConfirm"))) return;
    const { data } = await api.post(`/meetings/${id}/rotate-code`);
    setInfo((prev) => ({ ...prev, join_code: data.join_code }));
    toast.success(t("room.linkRotated"));
  };

  return (
    <div className="flex h-screen flex-col bg-vox-deep" data-testid="meeting-room-page">
      <header className="flex h-16 shrink-0 items-center justify-between gap-2 border-b border-vox-line bg-vox-bg px-3 sm:gap-4 sm:px-6">
        <div className="flex min-w-0 shrink items-center gap-3 sm:gap-4">
          <Wordmark brand={config.brand} to="/dashboard" />
          {info && <span className="hidden truncate text-sm text-vox-muted md:block" data-testid="room-title">{info.title}</span>}
          {info && (
            <span className="hidden shrink-0 items-center gap-2 rounded-full border border-vox-line px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider text-vox-dim sm:inline-flex" title={t("room.noLimit")} data-testid="room-elapsed-timer">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-breathe" /> {t("room.onAir")} {fmtClock(elapsed)}
            </span>
          )}
          {call && (
            <span className={`hidden items-center gap-2 rounded-full border px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider sm:inline-flex ${call.status === "ringing" ? "border-vox-bright/40 text-vox-bright" : call.status === "accepted" ? "border-emerald-500/40 text-emerald-300" : "border-vox-line text-vox-dim"}`} data-testid="call-status-chip">
              {call.status === "ringing" ? <><Loader2 size={12} className="animate-spin" /> {t("contacts.calling")} {call.to_name}…</> : t(`contacts.${call.status}`, call.status)}
              {call.status === "ringing" && <button type="button" onClick={cancelCall} className="ml-1 text-vox-dim hover:text-vox-text" title={t("contacts.cancel")} data-testid="call-cancel-button"><PhoneOff size={12} /></button>}
            </span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
          {info?.ai_enabled ? (
            rec.state === "recording" ? (
              <button type="button" onClick={rec.stop} className="btn-danger !border-vox-rec/60 !bg-vox-rec/20 animate-rec-pulse !px-3 sm:!px-4" data-testid="stop-recording-button">
                <Square size={12} fill="currentColor" /> <span className="font-mono" data-testid="recording-timer-display">{fmtClock(rec.seconds)}</span> <span className="hidden sm:inline">{t("room.recStop")}</span>
              </button>
            ) : (
              <button type="button" onClick={startRec} disabled={rec.state !== "idle"} className="btn-danger !px-3 sm:!px-4" data-testid="start-recording-button" title={t("room.recHint")}>
                {rec.state === "idle" ? <Circle size={12} fill="currentColor" /> : <Loader2 size={14} className="animate-spin" />}
                <span className="hidden sm:inline">{rec.state === "starting" ? t("room.starting") : rec.state === "finishing" ? t("room.finishing") : t("room.recStart")}</span>
              </button>
            )
          ) : info?.ai_consent_required ? (
            <Link to="/org" className="hidden items-center gap-2 rounded-full border border-vox-amber/30 px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider text-vox-amber sm:inline-flex" data-testid="ai-consent-required-notice" title={t("org.aiConsentHint")}>
              <ShieldAlert size={12} /> {t("org.aiConsentShort")}
            </Link>
          ) : info ? (
            <span className="hidden items-center gap-2 rounded-full border border-vox-amber/30 px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider text-vox-amber sm:inline-flex" data-testid="locked-ai-feature-notice" title={t("locked.desc")}>
              <Lock size={12} /> {t("locked.title")}
            </span>
          ) : null}
          {info && <button type="button" onClick={copyGuest} className="btn-ghost !px-3 !py-2" title={t("dashboard.guestLink")} data-testid="room-copy-guest-link"><Copy size={14} /></button>}
          {info?.is_moderator && <button type="button" onClick={rotateGuest} className="btn-ghost !px-3 !py-2" title={t("room.rotateLink")} data-testid="room-rotate-guest-link"><RefreshCw size={14} /></button>}
          {info?.is_moderator && <button type="button" onClick={endMeeting} className="btn-ghost !px-3 !py-2" title={t("room.end")} data-testid="end-meeting-button"><X size={14} /></button>}
          <button type="button" onClick={leave} className="btn-ghost !px-3 !py-2" title={t("room.leave")} data-testid="leave-room-button"><LogOut size={14} /></button>
        </div>
      </header>
      {rec.state === "recording" && (
        <div className="flex h-7 shrink-0 items-center justify-center gap-2 bg-vox-rec/15 font-mono text-[11px] uppercase tracking-widest text-[#ff9b9e]" data-testid="recording-status-indicator">
          <span className="h-1.5 w-1.5 rounded-full bg-vox-rec animate-breathe" /> {t("room.recording")}
        </div>
      )}
      <div className="relative flex-1">
        {error ? (
          <div className="grid h-full place-items-center text-sm text-[#ff9b9e]" data-testid="room-error">{error}</div>
        ) : !info ? (
          <div className="grid h-full place-items-center text-sm text-vox-muted"><span className="flex items-center gap-2"><Loader2 size={16} className="animate-spin" />{t("common.loading")}</span></div>
        ) : (
          <JitsiFrame domain={info.domain} roomName={info.room_name} jwt={info.jwt} displayName={info.display_name} email={info.email} subject={info.title} lobby={info.lobby_enabled && info.is_moderator} onLeave={leave} onApi={(j) => { jitsiRef.current = j; }} />
        )}
      </div>
    </div>
  );
}
